// Global state to persist progress between AJAX updates
if (typeof window.RewardTrackerState === 'undefined') {
  window.RewardTrackerState = {
    prevProgress: {} // Store previous progress values by reward index
  };
}

if (typeof RewardTracker !== 'function') {

  class RewardTracker extends HTMLElement {

    constructor() {
      super();
    }
    
    connectedCallback() {
      // Small delay to ensure the element is fully in DOM before animating
      requestAnimationFrame(() => {
        this.init();
      });
    }

    init() {
      const cartTotal = Number(this.getAttribute('data-reward-cart-total'));
      const currencyRate = Shopify.currency.rate ? Number(Shopify.currency.rate) : 1;
      
      // Get reward settings
      const freeShippingEnabled = this.getAttribute('data-free-shipping-enabled') === 'true';
      const freeShippingAmount = Number(this.getAttribute('data-free-shipping-amount')) * currencyRate;
      
      const freeGift1Enabled = this.getAttribute('data-free-gift-1-enabled') === 'true';
      const freeGift1Amount = Number(this.getAttribute('data-free-gift-1-amount')) * currencyRate;
      
      const freeGift2Enabled = this.getAttribute('data-free-gift-2-enabled') === 'true';
      const freeGift2Amount = Number(this.getAttribute('data-free-gift-2-amount')) * currencyRate;
      
      // Build rewards array
      const rewards = [];
      
      if (freeShippingEnabled) {
        rewards.push({
          type: 'shipping',
          amount: freeShippingAmount,
          remaining: freeShippingAmount - cartTotal,
          unlocked: cartTotal >= freeShippingAmount
        });
      }
      
      if (freeGift1Enabled) {
        rewards.push({
          type: 'gift',
          amount: freeGift1Amount,
          remaining: freeGift1Amount - cartTotal,
          unlocked: cartTotal >= freeGift1Amount
        });
      }
      
      if (freeGift2Enabled) {
        rewards.push({
          type: 'gift',
          amount: freeGift2Amount,
          remaining: freeGift2Amount - cartTotal,
          unlocked: cartTotal >= freeGift2Amount
        });
      }
      
      // Sort by amount (ascending)
      rewards.sort((a, b) => a.amount - b.amount);
      
      // Assign min values for each reward (range-based progress)
      rewards.forEach((reward, index) => {
        reward.min = index === 0 ? 0 : rewards[index - 1].amount;
      });
      
      // Find the closest unlockable reward (first one not unlocked)
      const nextReward = rewards.find(r => !r.unlocked);
      
      // Determine which rewards are unlocked
      const shippingUnlocked = rewards.find(r => r.type === 'shipping' && r.unlocked);
      const gift1Unlocked = freeGift1Enabled && rewards.some((r, i) => r.type === 'gift' && r.unlocked && rewards.filter(x => x.type === 'gift').indexOf(r) === 0);
      const gift2Unlocked = freeGift2Enabled && rewards.some((r, i) => r.type === 'gift' && r.unlocked && rewards.filter(x => x.type === 'gift').indexOf(r) === 1);
      const anyGiftUnlocked = gift1Unlocked || gift2Unlocked;
      const allUnlocked = rewards.length > 0 && rewards.every(r => r.unlocked);
      const noneUnlocked = rewards.length === 0 || rewards.every(r => !r.unlocked);
      
      // Remove all state classes first
      this.classList.remove(
        'reward-tracker--none-available',
        'reward-tracker--shipping-available',
        'reward-tracker--gift-1-available',
        'reward-tracker--gift-2-available',
        'reward-tracker--gift-available',
        'reward-tracker--all-available'
      );
      
      // Add appropriate state classes
      if (allUnlocked) {
        this.classList.add('reward-tracker--all-available');
      }
      if (noneUnlocked) {
        this.classList.add('reward-tracker--none-available');
      }
      if (shippingUnlocked) {
        this.classList.add('reward-tracker--shipping-available');
      }
      if (gift1Unlocked) {
        this.classList.add('reward-tracker--gift-1-available');
      }
      if (gift2Unlocked) {
        this.classList.add('reward-tracker--gift-2-available');
      }
      if (anyGiftUnlocked) {
        this.classList.add('reward-tracker--gift-available');
      }
      
      // Update message
      const messageEl = this.querySelector('[data-reward-message]');
      if (messageEl) {
        if (nextReward) {
          const remainingFormatted = this._formatMoney(Math.abs(nextReward.remaining), KROWN.settings.shop_money_format);
          if (nextReward.type === 'shipping') {
            messageEl.innerHTML = window.KROWN.settings.locales.reward_tracker_shipping_remaining.replace('{{ remaining_amount }}', remainingFormatted);
          } else {
            messageEl.innerHTML = window.KROWN.settings.locales.reward_tracker_gift_remaining.replace('{{ remaining_amount }}', remainingFormatted);
          }
        } else if (rewards.length > 0) {
          // All rewards unlocked
          messageEl.innerHTML = window.KROWN.settings.locales.reward_tracker_all_unlocked;
        }
      }
      
      // Update each reward card with its own progress
      const rewardElements = this.querySelectorAll('.reward-tracker__reward');
      rewardElements.forEach((el) => {
        const rewardIndex = el.getAttribute('data-reward-index');
        const rewardAmount = Number(el.getAttribute('data-reward-amount'));
        const rewardMin = Number(el.getAttribute('data-reward-min'));
        const progressBar = el.querySelector('[data-reward-progress]');
        
        // Calculate progress within this reward's range
        let progressPercent = 0;
        const range = rewardAmount - rewardMin;
        
        if (cartTotal >= rewardAmount) {
          // Fully unlocked
          progressPercent = 100;
          el.classList.add('reward-tracker__reward--unlocked');
        } else if (cartTotal > rewardMin) {
          // Partially filled (within this range)
          progressPercent = ((cartTotal - rewardMin) / range) * 100;
          el.classList.remove('reward-tracker__reward--unlocked');
        } else {
          // Not started (cart total below min)
          progressPercent = 0;
          el.classList.remove('reward-tracker__reward--unlocked');
        }
        
        if (progressBar) {
          const hasPrevious = window.RewardTrackerState.prevProgress.hasOwnProperty(rewardIndex);
          const prevProgress = hasPrevious ? window.RewardTrackerState.prevProgress[rewardIndex] : progressPercent;

          progressBar.style.transition = 'none';
          progressBar.style.width = `${prevProgress}%`;

          // Double rAF commits the initial state in one paint, then applies
          // the animated target on the next. Replaces a synchronous offsetWidth
          // reflow that was blocking input-to-paint on cart updates.
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              progressBar.style.transition = 'width 0.25s ease-out';
              progressBar.style.width = `${progressPercent}%`;
            });
          });

          window.RewardTrackerState.prevProgress[rewardIndex] = progressPercent;
        }
      });
    }
    
    _formatMoney(cents, format) {
      if (typeof cents === 'string') {
        cents = cents.replace('.', '');
      }

      let value = '';
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const formatString = format || moneyFormat;

      function formatWithDelimiters(number, precision, thousands, decimal) {
        thousands = thousands || ',';
        decimal = decimal || '.';

        if (isNaN(number) || number === null) {
          return 0;
        }

        number = (number / 100.0).toFixed(precision);

        const parts = number.split('.');
        const dollarsAmount = parts[0].replace(
          /(\d)(?=(\d\d\d)+(?!\d))/g,
          '$1' + thousands
        );
        const centsAmount = parts[1] ? decimal + parts[1] : '';

        return dollarsAmount + centsAmount;
      }

      switch (formatString.match(placeholderRegex)[1]) {
        case 'amount':
          value = formatWithDelimiters(cents, 2);
          break;
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
        case 'amount_no_decimals_with_space_separator':
          value = formatWithDelimiters(cents, 0, ' ');
          break;
        case 'amount_with_apostrophe_separator':
          value = formatWithDelimiters(cents, 2, "'");
          break;
      }

      return formatString.replace(placeholderRegex, value);
    }

  }

  if (typeof customElements.get('reward-tracker') == 'undefined') {
    customElements.define('reward-tracker', RewardTracker);
  }

}
